import type { ReactNode } from 'react';
import { Button, FileThumb, Surface } from '@nexvia-logistics/ui';
import { PencilSimple } from '@nexvia-logistics/ui/icons';
import { LockersIcon, MapPinIcon } from '@/shared/icons';

/**
 * NEX-1164 mockup-parity fix: the mockup's photo area always shows 3 slots (real photos, padded
 * with empty placeholders up to this count) — read `LocationForm.tsx`'s own docblock first if
 * tempted to add an upload affordance alongside these: uploading only ever happens in the create/
 * edit form, never from this list card, so these slots are purely decorative, no click handler.
 */
const PHOTO_SLOT_COUNT = 3;

/**
 * A saved depósito, as the Claude Design mock draws it (spec 2026-10-18 §3.4): a header with the
 * `Warehouse` glyph, the name as an `h2` and an "Editar" text button with a pencil on the right;
 * a hairline; then the address with a pin, the photo thumbnails and the service badges.
 *
 * NEX-1164 (2026-10-15, spec D-4.2): `AddressCard` itself is not adopted because it has no slot
 * for the three photo thumbnails or the service badges the mockup puts on each card, and its
 * `notes` slot is a `<p>` (block content inside it is invalid HTML) — noted as a package gap. So
 * this is a local organism built only from real parts (`Surface`, `Button`, `FileThumb`, glyphs).
 *
 * **NEX-1164 design pass (2026-10-18, decision D-4.1)**: no remove action and no "Predeterminada"
 * chip any more — the mock's card has neither. "Eliminar depósito" lives in the edit form
 * (`LocationForm`'s `onRemove`), with the same confirmation and API call as before. The upload box
 * the mock draws inside the card is not drawn either: there is no upload from the list.
 *
 * Photos: always renders `Math.max(PHOTO_SLOT_COUNT, photos?.length ?? 0)` slots — a `FileThumb`
 * with the photo's `url` wherever a photo exists, an `aria-hidden` `<li>` holding a `FileThumb`
 * with no `url` (the molecule's own decorative `Image` glyph, no button) everywhere it doesn't, up
 * to a minimum of 3 — so a location with 4+ photos still shows all of them (never truncated).
 */
export interface LocationCardProps {
  /** What the user called it, or the locality/department line — a card doesn't require a name. */
  name: ReactNode;
  /** The address itself, one line per row. */
  lines: readonly string[];
  onEdit?: () => void;
  /** The media slot `AddressCard` lacks (D-4.2): read-only photo thumbnails, as given. */
  photos?: readonly string[];
  /** The badges slot `AddressCard` lacks (D-4.2): pill/chip content rendered below the photos. */
  badges?: ReactNode;
  className?: string;
}

export function LocationCard({ name, lines, onEdit, photos, badges, className }: LocationCardProps) {
  const label = typeof name === 'string' ? name : 'la dirección';

  return (
    <Surface variant="outlined" padding="none" as="article" className={className}>
      <header className="flex items-center justify-between gap-3 px-5 py-4 shadow-[inset_0_-1px_0_var(--nx-outline-muted)]">
        <h2 className="m-0 flex min-w-0 items-center gap-2 text-ui-h3 text-on-surface">
          <LockersIcon size={18} className="shrink-0 text-primary" aria-hidden="true" />
          {name}
        </h2>
        {onEdit ? (
          <Button type="button" variant="text" size="xs" showArrow={false} onClick={onEdit} aria-label={`Editar ${label}`}>
            <PencilSimple size={14} aria-hidden="true" />
            Editar
          </Button>
        ) : null}
      </header>

      <div className="flex flex-col gap-3 px-5 py-4">
        {lines.map((line) => (
          <p key={line} className="m-0 flex items-center gap-1.5 text-ui-body text-on-surface-variant">
            <MapPinIcon size={14} className="shrink-0" aria-hidden="true" />
            {line}
          </p>
        ))}

        <ul className="m-0 flex list-none flex-wrap gap-2 p-0">
          {Array.from({ length: Math.max(PHOTO_SLOT_COUNT, photos?.length ?? 0) }, (_, index) => {
            const photo = photos?.[index];
            return (
              <li key={index} aria-hidden={photo ? undefined : true}>
                <FileThumb url={photo} label={`Foto ${index + 1} de ${label}`} />
              </li>
            );
          })}
        </ul>

        {badges ? <div className="flex flex-wrap items-center gap-1.5">{badges}</div> : null}
      </div>
    </Surface>
  );
}
